/**
 * Lista de outros planos no editor do estudo: o vendedor marca quais aparecem
 * na página "Onde o consórcio ganha" e pode trocar o plano do estudo por um
 * deles ("Usar"). A busca e a conta ficam no editor (alternativas.ts).
 */
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { formatBRL } from '@/lib/format';
import { NOME_ORDEM, chaveAlternativa, type Alternativa, type OrdemAlternativas } from './alternativas';

const pct = (v: number) => `${v.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;

type Props = {
  /** null enquanto carrega. */
  alternativas: Alternativa[] | null;
  falhou: boolean;
  sistema: 'SAC' | 'Price';
  ordem: OrdemAlternativas;
  onOrdem: (o: OrdemAlternativas) => void;
  selecionadas: ReadonlySet<string>;
  onAlternar: (chave: string) => void;
  onUsar: (a: Alternativa) => void;
  /** Chave do plano sendo carregado pelo "Usar". */
  usando: string | null;
};

const VISIVEIS = 8;

export const PainelAlternativas = ({ alternativas, falhou, sistema, ordem, onOrdem, selecionadas, onAlternar, onUsar, usando }: Props) => {
  const [todas, setTodas] = useState(false);

  if (falhou) return <p className="text-xs text-destructive">Não foi possível buscar outros planos.</p>;
  if (alternativas === null) {
    return (
      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" /> Buscando planos com crédito parecido...
      </p>
    );
  }
  if (alternativas.length === 0) return <p className="text-xs text-muted-foreground">Nenhum outro plano com crédito parecido e vaga.</p>;

  const lista = todas ? alternativas : alternativas.slice(0, VISIVEIS);
  return (
    <div className="space-y-2">
      <div role="group" aria-label="Ordenar planos" className="flex flex-wrap gap-1">
        {(Object.keys(NOME_ORDEM) as OrdemAlternativas[]).map((o) => (
          <Button key={o} type="button" size="sm" variant={o === ordem ? 'default' : 'outline'} className="h-7 px-2 text-xs" aria-pressed={o === ordem} onClick={() => onOrdem(o)}>
            {NOME_ORDEM[o]}
          </Button>
        ))}
      </div>
      <ul className="divide-y rounded-md border">
        {lista.map((a) => {
          const chave = chaveAlternativa(a);
          const nome = `Grupo ${a.grupo} · ${a.categoria}`;
          const nomeCompleto = `${nome}, ${a.prazo} meses`;
          return (
            <li key={chave} className={`flex items-center gap-2 px-2 py-2 ${a.atual ? 'bg-[#F3EDE3]/70 dark:bg-muted' : ''}`}>
              <Checkbox
                checked={selecionadas.has(chave)}
                onCheckedChange={() => onAlternar(chave)}
                aria-label={`Mostrar ${nomeCompleto} na apresentação`}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1 text-xs font-semibold">
                  <span className="truncate">{nome}</span>
                  {a.atual ? (
                    <span className="rounded bg-[#C4AD8E] px-1 text-[10px] text-[#1F2C40]">na apresentação</span>
                  ) : a.mesmoGrupo ? (
                    <span className="rounded bg-muted px-1 text-[10px]">mesmo grupo</span>
                  ) : null}
                </div>
                <div className="truncate text-[11px] text-muted-foreground">
                  {a.prazo} meses · {pct(a.taxaAdminPct)} · {formatBRL(a.credito)}
                </div>
              </div>
              <div className={`shrink-0 text-right text-xs font-bold ${a.economia >= 0 ? 'text-green-700 dark:text-green-400' : 'text-amber-700'}`}>
                {a.economia >= 0 ? '' : '−'}
                {formatBRL(Math.abs(a.economia))}
                <div className="text-[10px] font-normal text-muted-foreground">vs {sistema}</div>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 shrink-0 px-2 text-xs"
                disabled={a.atual || usando !== null}
                onClick={() => onUsar(a)}
                aria-label={`Usar ${nomeCompleto}`}
              >
                {usando === chave ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Usar'}
              </Button>
            </li>
          );
        })}
      </ul>
      {alternativas.length > VISIVEIS && (
        <button type="button" className="text-xs text-muted-foreground underline" onClick={() => setTodas((t) => !t)}>
          {todas ? 'Mostrar menos' : `Mostrar todos (${alternativas.length})`}
        </button>
      )}
    </div>
  );
};
